import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

import SvgPlayBack from "@/assets/icons/play-black.svg";
import SvgPyramid from "@/assets/icons/line-pyramid.svg";

import { showServerError } from "@/utils";
import { getEmployeesPersonalRate, postEmployeesPersonalRates } from "@/api/offers";
import { IPersonalRates, IRatesHistory } from "@/api/types/offers";

import PersonalRateInfo, { IPersonalRateInfo } from "@/components/offers/personal-rates/PersonalRateInfo.vue";
import PersonalRateHeader from "@/components/offers/personal-rates/PersonalRateHeader.vue";
import FormDeletePersonalRate from "@/components/offers/personal-rates/FormDeletePersonalRate.vue";
import PersonalRateHistory from "@/components/offers/personal-rates/PersonalRateHistory.vue";

import PageLayout from "@/components/layout/PageLayout.vue";
import Status from "@/components/base/Status.vue";
import TrashButton from "@/components-UI/buttons/TrashButton.vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";

import orderBy from "lodash-es/orderBy";
import { getCurrentDate } from "@/utils/formatDates";
import { NotificationMessage } from "@/utils/notification";
import { translatePersonalRateStatus } from "@/utils/translate";
import { useWindowResize } from "@/utils/useWindowSize";
import {
    getPersonalRateAmounts,
    IPersonalRateAmounts,
    isPersonalRateChanged,
    isPersonalRateDueNow,
    isPersonalRateUpdateApplied,
    waitPersonalRateUpdate,
} from "@/utils/personalRate/personalRateUpdate";
import { onBeforeRouteUpdate, useRoute, useRouter } from "vue-router/composables";
import store from "@/store";

// --- store ---

export default /*#__PURE__*/_defineComponent({
  __name: 'PersonalRate',
  setup(__props) {

const toggleEditingMode = (val: boolean) => store.commit("TOGGLE_EDITING_MODE", val);
const editingMode = computed(() => store.getters["GET_EDITING_MODE"]);

// --- router ---
const route = useRoute();
const router = useRouter();
let beforeListRoute = "/offers/personal-rates";

// --- state ---
const personalRate = ref({} as IPersonalRates);
const personalRateId = ref("");
const isDeletePersonalRate = ref(false);
const isNoNeedNotify = ref(false);
// Пока правка применяется, вход в новую закрыт: её сравнили бы со старыми суммами с сервера
const isUpdateApplying = ref(false);

// Суммы, как их отдал сервер: с ними сравнивается правка перед отправкой
let savedAmounts: IPersonalRateAmounts = { revenue: null, payout: null };
let updateWaiting: AbortController | null = null;

const settings = ref({
    title: "История изменений",
    loading: true,
});

// --- computed ---
const title = computed(() => `Индивидуальная цена ${personalRateId.value}`);

const ratesHistory = computed<IRatesHistory[]>(() =>
    orderBy(personalRate.value.rates_history, ["date"], ["desc"]),
);

const isPersonalRateEmpty = computed(() => Object.keys(personalRate.value).length > 0);

const { width } = useWindowResize();
const isMobile = computed(() => width.value < 768);

const getButtonContentVariant = computed(() =>
    isMobile.value ? "iconOnly" : "withIcon",
);

// --- methods ---
function editPersonalRate(item: IPersonalRateInfo): void {
    personalRate.value.personal_rates[item.key].amount = item.value;
}

function updateTime(value: string): void {
    personalRate.value.personal_rates.start_date = value;
}

function showNotification(text: string, type: string): void {
    new NotificationMessage(text, type);
}

function deletePersonalRate(): void {
    showNotification(`Цена ${personalRate.value.personal_rates.id} удалена`, "info");
    isDeletePersonalRate.value = false;
    router.push({ name: "personal-rates" });
}

async function activatePersonalRate(): Promise<void> {
    try {
        await postEmployeesPersonalRates({
            pairs: [
                {
                    offer_id: personalRate.value.offer.legacy_id,
                    goal_id: personalRate.value.goal.legacy_id,
                    affiliate_id: personalRate.value.affiliate.legacy_id,
                },
            ],
        });
        showNotification(
            `Цена ${personalRate.value.personal_rates.id} будет активирована в ближайшее время`,
            "success",
        );
        await getPersonalRate();
    } catch (err) {
        showServerError(err, "Индивидуальная цена не активирована");
    }
}

function isPersonalRateEdited(): boolean {
    return isPersonalRateChanged(savedAmounts, getPersonalRateAmounts(personalRate.value));
}

async function updatePersonalRate(): Promise<void> {
    if (editingMode.value && !isPersonalRateEdited()) {
        // Суммы не менялись: бэкенд создал бы на правку задачу и уведомление партнёру.
        // Цену перечитываем, чтобы сбросить дату, выбранную без смены сумм
        isNoNeedNotify.value = false;
        await getPersonalRate();
    } else if (editingMode.value) {
        try {
            const knownTaskIds = personalRate.value.rates_history.map(({ id }) => id);
            const rate: any = {
                revenue: personalRate.value.personal_rates.revenue.amount,
                payout: personalRate.value.personal_rates.payout.amount,
                start_date: personalRate.value.personal_rates.start_date,
                offer_id: personalRate.value.offer.legacy_id,
                goal_id: personalRate.value.goal.legacy_id,
                affiliate_id: personalRate.value.affiliate.legacy_id,
            };

            await postEmployeesPersonalRates({
                pairs: [rate],
                need_notify: !isNoNeedNotify.value,
            });

            showNotification(
                `Цена ${personalRate.value.personal_rates.id} отредактирована`,
                "success",
            );

            waitPersonalRateApplied(rate.start_date, knownTaskIds);
        } catch (err) {
            showServerError(err, "Ошибка сохранения");
        } finally {
            isNoNeedNotify.value = false;
        }
    } else {
        personalRate.value.personal_rates.start_date = getCurrentDate("yyyy-MM-dd HH:mm");
    }
    toggleEditingMode(!editingMode.value);
}

// POST правки только ставит задачу в очередь offers-service, цену переписывает консьюмер.
// До этого GET отдаёт старые суммы, поэтому на экране остаются введённые, пока правка не применится
async function waitPersonalRateApplied(startDate: string, knownTaskIds: number[]): Promise<void> {
    if (!isPersonalRateDueNow(startDate)) {
        await getPersonalRate();
        return;
    }

    updateWaiting?.abort();
    const waiting = new AbortController();
    updateWaiting = waiting;
    settings.value.loading = true;
    isUpdateApplying.value = true;
    try {
        const { rate, isApplied } = await waitPersonalRateUpdate(
            () => getEmployeesPersonalRate(personalRateId.value),
            (loaded) => isPersonalRateUpdateApplied(loaded, knownTaskIds),
            { signal: waiting.signal },
        );
        if (waiting.signal.aborted) {
            return;
        }
        if (rate) {
            setPersonalRate(rate);
        }
        if (!isApplied) {
            showNotification(`Цена ${personalRate.value.personal_rates.id} будет применена в ближайшее время`, "info");
        }
    } catch (err) {
        showServerError(err, "Индивидуальная цена не найдена");
    } finally {
        if (updateWaiting === waiting) {
            updateWaiting = null;
            settings.value.loading = false;
            isUpdateApplying.value = false;
        }
    }
}

function setPersonalRate(rate: IPersonalRates): void {
    personalRate.value = rate;
    personalRate.value.rates_history = personalRate.value.rates_history.reverse();
    savedAmounts = getPersonalRateAmounts(rate);
}

async function getPersonalRate(): Promise<void> {
    settings.value.loading = true;
    try {
        setPersonalRate(await getEmployeesPersonalRate(personalRateId.value));
    } catch (err) {
        showServerError(err, "Индивидуальная цена не найдена");
    } finally {
        settings.value.loading = false;
    }
}

function backToList(): void {
    router.push({ path: beforeListRoute });
}

function redirectToPrivateRates(): void {
    const { offer, goal, affiliate } = personalRate.value;
    const affiliateId = affiliate.buffer_account_id ?? affiliate.legacy_id;
    const query = {
        rate_status: ["active"],
        offer_id: offer.legacy_id,
        goal_id: goal.legacy_id,
        affiliate_id: affiliateId,
    };
    const path = router.resolve({ name: 'private-rates', query });
    window.open(path.href, '_blank');
}

// --- lifecycle ---
onBeforeRouteUpdate((to, from, next) => {
    next(() => {
        if (from.fullPath !== "/") {
            beforeListRoute = from.fullPath;
        }
    });
});

onMounted(() => {
    toggleEditingMode(false);
    personalRateId.value = route.params.id as string;
    getPersonalRate();
    document.title = `Цена ${personalRateId.value}`;
});

onBeforeUnmount(() => {
    updateWaiting?.abort();
    toggleEditingMode(true);
});

return { __sfc: true,toggleEditingMode, editingMode, route, router, beforeListRoute, personalRate, personalRateId, isDeletePersonalRate, isNoNeedNotify, isUpdateApplying, savedAmounts, updateWaiting, settings, title, ratesHistory, isPersonalRateEmpty, width, isMobile, getButtonContentVariant, editPersonalRate, updateTime, showNotification, deletePersonalRate, activatePersonalRate, isPersonalRateEdited, updatePersonalRate, waitPersonalRateApplied, setPersonalRate, getPersonalRate, backToList, redirectToPrivateRates, SvgPlayBack, SvgPyramid, PersonalRateInfo, PersonalRateHeader, FormDeletePersonalRate, PersonalRateHistory, PageLayout, Status, TrashButton, BaseButtonV2, translatePersonalRateStatus }
}

})